import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { getIncompleteSpecializedVouchers } from '../../../services/dashboardService';
import { SpecializedVoucherItem } from '../../../types/dashboard';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const IncompleteSpecializedVouchersWidget: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<SpecializedVoucherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getIncompleteSpecializedVouchers();
      setData(result);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل السندات المتخصصة الغير مكتملة');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewVoucher = (voucherId: string) => {
    navigate(`/finance/specialized-vouchers/${voucherId}`);
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #9b59b6',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: '#9b59b6',
              mr: 2
            }}
          >
            📋
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
              السندات المتخصصة
            </Typography>
            <Typography variant="body2" sx={{ color: '#95a5a6' }}>
              <Chip 
                label={`${data.length} سندات`} 
                size="small" 
                sx={{ 
                  bgcolor: '#fef5e7', 
                  color: '#f39c12',
                  fontWeight: 'bold'
                }} 
              /> غير مكتملة
            </Typography>
          </Box>
        </Box>

        {/* Voucher List */}
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>✅</Typography>
            <Typography color="textSecondary">لا توجد سندات متخصصة غير مكتملة</Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {data.slice(0, 5).map((voucher) => (
              <ListItem
                key={voucher._id}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#e9ecef',
                    transform: 'translateX(-5px)'
                  }
                }}
                onClick={() => handleViewVoucher(voucher._id)}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                      سند متخصص #{voucher.voucherNumber}
                      {voucher.branchName && (
                        <Chip
                          label={voucher.branchName}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#e3f2fd',
                            color: '#1976d2'
                          }}
                        />
                      )}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                        📅 {format(new Date(voucher.date), 'yyyy-MM-dd', { locale: ar })} | نوع: {voucher.type}
                      </Typography>
                      {voucher.description && (
                        <Typography variant="caption" sx={{ color: '#7f8c8d', display: 'block', mt: 0.5 }}>
                          {voucher.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9b59b6' }}>
                  {voucher.amount.toLocaleString('ar-SA')} ر.س
                </Typography>
              </ListItem>
            ))}
          </List>
        )}

        {data.length > 5 && (
          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/finance/specialized-vouchers')}
            sx={{
              mt: 2,
              borderColor: '#9b59b6',
              color: '#9b59b6',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: '#8e44ad',
                bgcolor: 'rgba(155, 89, 182, 0.05)'
              }
            }}
          >
            عرض جميع السندات ({data.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default IncompleteSpecializedVouchersWidget;